import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api } from "services/api/api.service";
import type {
  StaffProfileData,
  StaffProfileEmploymentCurrent,
} from "services/api/company/staff.service";
import { mapStaffProfileResponseV3ToData } from "services/api/company/staff.service";
import { formatDuration } from "utils/utils";

interface UseStaffProfilePanelLogicParams {
  companyId: number | null;
  isOpen: boolean;
  workerId: number | null;
  onSaved?: () => void;
}

interface UseStaffProfilePanelLogicResult {
  profile: StaffProfileData | null;
  isSelfRegistered: boolean;
  isLoading: boolean;
  hasLoadError: boolean;
  fullName: string;
  showMissingPersonalNumberWarning: boolean;
  currentEmployment: StaffProfileEmploymentCurrent | null;
  teamLabels: string[];
  roleLabels: string[];
  primaryTeamLabel: string | null;
  primaryRoleLabel: string | null;
  additionalTagCount: number;
  hasStatistics: boolean;
  ratingLabel: string;
  ratingCountLabel: string;
  workedHoursLabel: string;
  reloadProfile: () => void;
}

const normalizeStaffProfile = (raw: StaffProfileData): StaffProfileData => {
  const currentEmployment = raw.employmentInfo?.current;

  if (!currentEmployment) {
    return {
      ...raw,
      workPercentage: raw.workPercentage ?? 1,
    };
  }

  const normalizedEndDate =
    currentEmployment.endDate != null &&
    currentEmployment.endDate.includes("9999-12-31")
      ? null
      : currentEmployment.endDate;

  const normalizedTaxTable =
    currentEmployment.taxTable === 0 ? 36 : currentEmployment.taxTable;

  return {
    ...raw,
    workPercentage: raw.workPercentage ?? 1,
    employmentInfo: {
      endDate: raw.employmentInfo?.endDate ?? null,
      current: {
        ...currentEmployment,
        endDate: normalizedEndDate,
        taxTable: normalizedTaxTable,
      },
    },
  };
};

/** Encapsulates data fetching, normalization, and computed view labels for the staff profile panel. */
export const useStaffProfilePanelLogic = (
  params: UseStaffProfilePanelLogicParams,
): UseStaffProfilePanelLogicResult => {
  const [profile, setProfile] = useState<StaffProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const requestVersionRef = useRef(0);

  useEffect(() => {
    requestVersionRef.current += 1;
    const requestVersion = requestVersionRef.current;
    let isCancelled = false;

    if (!params.isOpen || params.companyId == null || params.workerId == null) {
      setProfile(null);
      setIsLoading(false);
      setHasLoadError(false);
      return;
    }
    const companyId = params.companyId;
    const workerId = params.workerId;

    setIsLoading(true);
    setHasLoadError(false);

    const loadProfile = async (): Promise<void> => {
      try {
        const response = await Api().company.staff.getWorkerProfile(
          companyId,
          workerId,
        );
        if (isCancelled || requestVersion !== requestVersionRef.current) return;

        const mappedProfile = mapStaffProfileResponseV3ToData(response.data);
        setProfile(normalizeStaffProfile(mappedProfile));
      } catch {
        if (isCancelled || requestVersion !== requestVersionRef.current) return;
        setHasLoadError(true);
        setProfile(null);
      } finally {
        if (isCancelled || requestVersion !== requestVersionRef.current) return;
        setIsLoading(false);
      }
    };

    void loadProfile();

    return () => {
      isCancelled = true;
    };
  }, [params.companyId, params.isOpen, params.workerId, reloadVersion]);

  const onSaved = params.onSaved;
  const reloadProfile = useCallback((): void => {
    setReloadVersion((previousValue) => previousValue + 1);
    onSaved?.();
  }, [onSaved]);

  const activeProfile = profile;

  const fullName = useMemo(() => {
    const firstName = activeProfile?.firstName ?? "";
    const lastName = activeProfile?.lastName ?? "";
    const resolvedName = `${firstName} ${lastName}`.trim();
    if (resolvedName.length > 0) return resolvedName;
    if (params.workerId != null) return String(params.workerId);
    return "-";
  }, [activeProfile?.firstName, activeProfile?.lastName, params.workerId]);

  const showMissingPersonalNumberWarning =
    !isLoading &&
    !hasLoadError &&
    activeProfile != null &&
    !activeProfile.personalIdentityNumber;

  const teamLabels = useMemo<string[]>(() => {
    if (!activeProfile) return [];

    const uniqueTeamLabels = new Set<string>();
    activeProfile.workGroups.forEach((workGroup) => {
      if (!workGroup.name) return;
      uniqueTeamLabels.add(workGroup.name);
    });

    return Array.from(uniqueTeamLabels);
  }, [activeProfile]);

  const roleLabels = useMemo<string[]>(() => {
    const rawRole = activeProfile?.employmentInfo?.current?.role;
    if (!rawRole) return [];

    const uniqueRoleLabels = new Set<string>();
    rawRole
      .split(/[;,]/)
      .filter((value) => value.length > 0)
      .forEach((value) => uniqueRoleLabels.add(value));

    return Array.from(uniqueRoleLabels);
  }, [activeProfile]);

  const additionalTagCount =
    Math.max(teamLabels.length - 1, 0) + Math.max(roleLabels.length - 1, 0);

  const statsByName = useMemo<Record<string, number>>(() => {
    if (!activeProfile) return {};

    return activeProfile.stats.reduce<Record<string, number>>(
      (accumulator, item) => {
        accumulator[item.stat] = item.value;
        return accumulator;
      },
      {},
    );
  }, [activeProfile]);

  return {
    profile: activeProfile,
    isSelfRegistered: activeProfile?.isRegistered === true,
    isLoading,
    hasLoadError,
    fullName,
    showMissingPersonalNumberWarning,
    currentEmployment: activeProfile?.employmentInfo?.current ?? null,
    teamLabels,
    roleLabels,
    primaryTeamLabel: teamLabels[0] ?? null,
    primaryRoleLabel: roleLabels[0] ?? null,
    additionalTagCount,
    hasStatistics: activeProfile ? activeProfile.stats.length > 0 : false,
    ratingLabel: activeProfile ? `${statsByName.Rating}/5` : "-",
    ratingCountLabel: activeProfile ? String(statsByName.AmountOfRatings) : "-",
    workedHoursLabel: activeProfile
      ? formatDuration(String(statsByName.AmountOfHoursWorked))
      : "-",
    reloadProfile,
  };
};
